
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const BottomNav: React.FC = () => {
  const { t } = useTranslation();
  const { subscriptionStatus } = useAuth();
  const location = useLocation();
  const isPremium = subscriptionStatus === 'active' || subscriptionStatus === 'trialing' || subscriptionStatus === 'pro' || localStorage.getItem('aura_demo_mode') === 'true';
  const [dietStatus, setDietStatus] = useState<string | null>(localStorage.getItem('aura_diet_status'));
  const [now, setNow] = useState(Date.now());

  // Sync Diet Status from LocalStorage & Real-time Reactivity
  useEffect(() => {
    const syncStatus = () => {
      console.log("[BottomNav] Syncing diet status...");
      setDietStatus(localStorage.getItem('aura_diet_status'));
    };

    // V375: Immediate update on global event
    window.addEventListener('aura_diet_updated', syncStatus);

    // Polling fallback and Real-time Timer Tick
    const interval = setInterval(() => {
      setNow(Date.now()); // Force re-render for timer checks
      const current = localStorage.getItem('aura_diet_status');
      if (current !== dietStatus) {
        setDietStatus(current);
      }
    }, 2000);

    return () => {
      window.removeEventListener('aura_diet_updated', syncStatus);
      clearInterval(interval);
    };
  }, [dietStatus]);

  const links = [
    { to: '/dashboard', icon: 'home', label: t('nav.home'), id: 'nav-item-home' },
    { to: '/calendar', icon: 'calendar_month', label: t('nav.calendar'), id: 'nav-item-calendar' },
    { to: '/routine-selector', icon: 'fitness_center', label: t('nav.routines'), id: 'nav-item-workout' },
    { to: '/nutrition-dashboard', icon: 'restaurant', label: t('nav.diet'), isPro: true, id: 'nav-item-diet' },
    { to: '/milestones', icon: 'query_stats', label: t('nav.milestones'), isPro: true, id: 'nav-item-milestones' },
  ];

  const isGeneratingDiet = (() => {
    // Stop strictly on idle or errors
    if (dietStatus === 'idle' || dietStatus === 'error_generation' || dietStatus === 'error_finalization') {
        return false;
    }

    // Check if the process has visually started yet
    const start = localStorage.getItem('aura_diet_process_start');
    if (start && start !== 'null' && start !== 'undefined' && start !== '') {
      const pStart = parseInt(start, 10);
      if (!isNaN(pStart) && now < pStart) {
        // The process has not visually started yet!
        return false;
      }
    }

    // 1. Check if there's an active enforced wait timer (Premium illusion block)
    const target = localStorage.getItem('aura_target_completion_time');
    
    if (target && target !== 'null' && target !== 'undefined' && target !== '') {
      const pTarget = parseInt(target, 10);
      if (!isNaN(pTarget) && now < pTarget) {
        // Timer is strictly in the future, keep animating even if backend finished early
        return true; 
      }
    }

    // If timer is expired or doesn't exist, stop animating if completed
    if (dietStatus === 'completed') {
        return false;
    }

    // 2. Fallback: If no timer or timer expired, verify if the literal state is still stuck generating
    return dietStatus === 'processing' || dietStatus === 'generating_initial' || dietStatus === 'updating' || dietStatus === 'trigger_update';
  })();

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-[9999] premium-glass shadow-[0_-10px_40px_rgba(0,0,0,0.5)]"
      style={{ 
        transform: 'translate3d(0,0,0)', 
        WebkitTransform: 'translate3d(0,0,0)', 
        willChange: 'transform',
        paddingBottom: 'max(0px, calc(env(safe-area-inset-bottom) - 12px))'
      }}
    >
      <div className="max-w-md mx-auto flex justify-between items-center px-4 pt-1.5 pb-1">
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            id={link.id}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 transition-all duration-500 relative group w-16 ${isActive ? 'text-primary' : 'text-text-muted hover:text-white'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className={`size-11 rounded-2xl flex items-center justify-center transition-all duration-300 relative ${isActive ? 'bg-white/5 shadow-[0_0_20px_rgba(242,127,13,0.15)] ring-1 ring-white/10' : 'group-hover:bg-white/5'}`}>

                  {/* GENERATING DIET INDICATOR (Only for Premium Users) */}
                  {link.to === '/nutrition-dashboard' && isGeneratingDiet && (
                    <div className="absolute inset-0 z-20 pointer-events-none">
                      <div className="absolute inset-0 rounded-2xl border-2 border-amber-400/50 animate-[spin_3s_linear_infinite] shadow-[0_0_10px_rgba(251,191,36,0.5)]"></div>
                      <div className="absolute -top-1 -right-1 size-2.5 bg-amber-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(251,191,36,1)]"></div>
                    </div>
                  )}

                  <span className={`material-symbols-outlined text-[26px] transition-transform duration-300 ${isActive ? 'filled scale-110 drop-shadow-[0_0_8px_rgba(242,127,13,0.5)]' : 'group-hover:scale-105'}`}>
                    {link.icon}
                  </span>

                  {/* PRO BADGE - LUXURY TAG (Offset to avoid icon overlap) */}
                  {(link as any).isPro && !isPremium && (
                    <div className="absolute -top-1 -right-3 premium-gold-gradient animate-premium-shine px-1.5 py-0.5 rounded-lg border border-black/10 shadow-[0_2px_10px_rgba(212,175,55,0.4)] flex items-center justify-center scale-90 z-30 pointer-events-none">
                      <span className="text-black text-[7px] font-black uppercase tracking-tighter">PRO</span>
                    </div>
                  )}

                  {/* ACTIVE INDICATOR GLOW */}
                  {isActive && (
                    <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full -z-10 animate-pulse-soft"></div>
                  )}
                </div>
                <span className={`text-[8px] font-bold uppercase tracking-[0.1em] transition-all duration-300 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-60 translate-y-0.5'}`}>
                  {link.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

export default BottomNav;
